/**
 * Base filter: show a menu item only if the user holds its required permission slug.
 * Items with no `permission` field are always shown (backwards compatible).
 */
const filterMenuItemsByPermission = (menuItems, userPermissions = []) => {
  if (!menuItems || !Array.isArray(menuItems)) return []

  return menuItems.filter(item => {
    if (!item.permission) return true
    return userPermissions.some(
      p => (typeof p === 'string' ? p : p?.slug) === item.permission,
    )
  })
}

/**
 * Menu keys hidden for customer-portal users in every layout.
 * These users have dedicated portal views for these features and must not
 * see the back-office equivalents even if they hold the underlying permission
 * (e.g. workorder_create for WO requests).
 */
const HIDDEN_FOR_CUSTOMER = [
  'workorderlist',
  'proposals',
  'proposalApprovementList',
  'workOrderCustomerStatuss',
]

const hasSlug = (permissions, slug) =>
  permissions.some(p => (typeof p === 'string' ? p : p?.slug) === slug)

/**
 * Full permission-based menu filter.
 *
 * Applies two permission rules in sequence:
 *  1. Standard: hide items whose required permission slug the user does not hold.
 *  2. Portal context: if the user holds `customer_dashboard`, additionally hide
 *     back-office items that customer-portal users must never reach.
 *
 * Both checks are purely slug-based — no role names are involved.
 *
 * @param {Array} menuItems
 * @param {Array} userPermissions - Array of { slug } objects or plain strings
 * @returns {Array} Filtered menu items
 */
export const filterMenuByPermissions = (menuItems, userPermissions = []) => {
  const filtered = filterMenuItemsByPermission(menuItems, userPermissions)
  const isCustomerPortalUser = hasSlug(userPermissions, 'customer_dashboard')
  if (!isCustomerPortalUser) return filtered
  return filtered.filter(item => !HIDDEN_FOR_CUSTOMER.includes(item.key))
}

export default filterMenuByPermissions
